import { create } from 'zustand';
import {
  canExport,
  consumeExport,
  exportsLeft,
  licenseLabel,
  readQuota,
  verifyToken,
  writeQuota,
  LICENSE_KEY,
  LICENSE_PUBLIC_KEY,
  LICENSE_SERVICE_ORIGIN,
  QUOTA_KEY,
  type License,
  type QuotaState,
} from '@nextcollage/engine';

/**
 * What this browser is allowed to do, and how it found out.
 *
 * Deliberately not part of the document store: a licence is not something you
 * can undo, and it does not belong in the history stack. It is also the one
 * piece of state that has to mean the same thing here and on a phone, which is
 * why every rule about it lives in the engine and this file only knows about
 * localStorage.
 *
 * Checking a saved code is arithmetic against a key already in this bundle —
 * no request, no service to be down, and nothing to fall back on if it is.
 */

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private browsing, or a full quota — the session still works */
  }
}

/**
 * Where the site sends someone to pay. A plain top-level navigation rather
 * than an embedded checkout, which keeps the content security policy at one
 * added origin instead of five.
 */
export const CHECKOUT_URL = 'https://nextcollage.lemonsqueezy.com/buy/REPLACE_ME';

/** What the app costs, written once so the paywall and the guide cannot drift. */
export const PRICE = '$4.99';

interface LicenseState {
  license: License | null;
  quota: QuotaState;
  paywallOpen: boolean;

  /** Exports still free here. `Infinity` once a code is in. */
  left: () => number;
  /** The `#a3f9c1` shown in the UI, or null. */
  label: () => string | null;

  /**
   * Ask permission to export. Returns false and raises the paywall when the
   * three free ones are gone, so a caller reads as `if (!allow()) return;`.
   */
  allow: () => boolean;
  /** Count an export that actually happened. */
  spend: () => void;

  /** Check a pasted code and keep it if it holds up. */
  redeem: (code: string) => boolean;
  /** Trade a completed checkout for a code. Rejects with a readable message. */
  claim: (reference: string) => Promise<void>;

  setPaywall: (open: boolean) => void;
  forget: () => void;
}

export const useLicense = create<LicenseState>((set, get) => ({
  license: verifyToken(read(LICENSE_KEY) ?? '', LICENSE_PUBLIC_KEY),
  quota: readQuota(read(QUOTA_KEY)),
  paywallOpen: false,

  left: () => exportsLeft(get().quota, get().license !== null),
  label: () => {
    const license = get().license;
    return license ? licenseLabel(license) : null;
  },

  allow() {
    if (canExport(get().quota, get().license !== null)) return true;
    set({ paywallOpen: true });
    return false;
  },

  spend() {
    const next = consumeExport(get().quota, get().license !== null);
    if (next === get().quota) return;
    write(QUOTA_KEY, writeQuota(next));
    set({ quota: next });
  },

  redeem(code) {
    const license = verifyToken(code, LICENSE_PUBLIC_KEY);
    if (!license) return false;
    write(LICENSE_KEY, code.replace(/\s+/g, ''));
    set({ license, paywallOpen: false });
    return true;
  },

  async claim(reference) {
    const response = await fetch(`${LICENSE_SERVICE_ORIGIN}/mint/web`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reference }),
    });

    const body = (await response.json().catch(() => ({}))) as { token?: string; error?: string };
    if (!response.ok || !body.token) {
      throw new Error(body.error ?? 'That purchase could not be confirmed.');
    }
    if (!get().redeem(body.token)) {
      throw new Error('The unlock code that came back could not be read.');
    }
  },

  setPaywall: (paywallOpen) => set({ paywallOpen }),

  /** Only for the "not my purchase" case, and for testing the paywall. */
  forget() {
    try {
      localStorage.removeItem(LICENSE_KEY);
    } catch {
      /* nothing to remove */
    }
    set({ license: null });
  },
}));
